import { QueryClient } from '@tanstack/react-query';

/**
 * Utility functions for cleaning up user-specific data during auth state changes
 */

/**
 * Clear all user-specific React Query cache data
 */
export const clearUserDataFromCache = (queryClient: QueryClient): void => {
  console.log('[Auth Cleanup] Starting selective cache cleanup');
  
  // Clear user-specific queries with specific patterns
  queryClient.removeQueries({ queryKey: ['user-favorites'] });
  queryClient.removeQueries({ queryKey: ['favorite-counts'] });
  queryClient.removeQueries({ queryKey: ['app-items'] });
  
  // Clear copilot data
  queryClient.removeQueries({ queryKey: ['copilot'] });
  
  // Clear workspace-related data
  queryClient.removeQueries({ queryKey: ['dashboard-data'] });
  queryClient.removeQueries({ queryKey: ['contacts'] });
  queryClient.removeQueries({ queryKey: ['companies'] });
  queryClient.removeQueries({ queryKey: ['pipeline-data'] });
  queryClient.removeQueries({ queryKey: ['fulfillment-data'] });
  queryClient.removeQueries({ queryKey: ['crm_projects'] });
  queryClient.removeQueries({ queryKey: ['tasks'] });
  queryClient.removeQueries({ queryKey: ['team-management'] });
  queryClient.removeQueries({ queryKey: ['customization'] });
  queryClient.removeQueries({ queryKey: ['planning-data'] });
  queryClient.removeQueries({ queryKey: ['columnConfig'] });
  
  // Clear user-specific data
  queryClient.removeQueries({ queryKey: ['notifications'] });
  queryClient.removeQueries({ queryKey: ['user-tasks'] });
  queryClient.removeQueries({ queryKey: ['files'] });
  queryClient.removeQueries({ queryKey: ['research'] });
  
  // Clear user profile data - use new query key
  queryClient.removeQueries({ queryKey: ['user-profile'] });
  queryClient.removeQueries({ queryKey: ['user-profile-images'] });
  
  // Remove old profile query keys (cleanup)
  queryClient.removeQueries({ queryKey: ['unified-profile'] });
  queryClient.removeQueries({ queryKey: ['profile'] });
  
  console.log('[Auth Cleanup] Completed selective cache cleanup');
};

/**
 * Clear user-specific localStorage items
 */
export const clearUserDataFromLocalStorage = (): void => {
  const itemsToRemove = [
    'taskViewMode',
    'imageFilters',
    'privateImageFilters',
    'copilotSidebarState'
  ];
  
  // Keep lastAuthPage to maintain user preference
  itemsToRemove.forEach(item => {
    try {
      localStorage.removeItem(item);
    } catch (error) {
      console.warn(`[Auth Cleanup] Failed to remove ${item} from localStorage:`, error);
    }
  });
  
  console.log('[Auth Cleanup] Cleared user-specific localStorage items');
};

/**
 * Perform comprehensive user session cleanup with debouncing
 */
let cleanupTimeoutId: NodeJS.Timeout | null = null;

export const performUserSessionCleanup = (queryClient: QueryClient): void => {
  // Debounce cleanup operations to prevent excessive calls
  if (cleanupTimeoutId) {
    clearTimeout(cleanupTimeoutId);
  }
  
  cleanupTimeoutId = setTimeout(() => {
    console.log('[Auth Cleanup] Starting comprehensive user session cleanup');
    
    // Clear React Query cache selectively
    clearUserDataFromCache(queryClient);
    
    // Clear localStorage
    clearUserDataFromLocalStorage();
    
    console.log('[Auth Cleanup] User session cleanup completed');
    cleanupTimeoutId = null;
  }, 300); // 300ms debounce
};
